
import React, { useState, useEffect } from 'react';
import { X, Database, Eye, Plus, Save, ArrowLeft, ArrowRight, ArrowRightLeft, Spline, Combine, Layers, Link2 } from 'lucide-react';

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');

const DataCombineModal = ({ isOpen, onClose, tables, onSaveDataset }) => {
    const [step, setStep] = useState(1); // 1: Config, 2: Preview

    // Config State
    const [combineMode, setCombineMode] = useState('join'); // 'join' | 'union'

    // Multi-Join State
    const [baseTable, setBaseTable] = useState('');
    const [joins, setJoins] = useState([]); // [{ id: 1, type: 'inner', table: '', leftKey: '', rightKey: '' }]

    // Union State (still 2 tables for now, can be expanded later if needed)
    // Union State
    const [unionTables, setUnionTables] = useState(['', '']); // Start with 2 slots
    // columnMapping: [{ target: 'colName', sources: ['colA', 'colB', ...] }]
    const [columnMapping, setColumnMapping] = useState([]);

    // Preview/Output State
    const [previewData, setPreviewData] = useState([]);
    const [generatedSql, setGeneratedSql] = useState('');
    const [newDatasetName, setNewDatasetName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Metadata


    // Reset when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setBaseTable('');
            setJoins([]);
            setUnionTables(['', '']);
            setColumnMapping([]);
            setPreviewData([]);
            setGeneratedSql('');
            setError('');
            setNewDatasetName('');
        }
    }, [isOpen]);

    // Helper to get columns for a specific table
    const getTableColumns = (tableName) => {
        if (!tableName) return [];
        const t = tables.find(tbl => tbl.name === tableName);
        return t ? t.columns : [];
    };

    // Auto-map for Union
    useEffect(() => {
        if (combineMode !== 'union') return;

        // If mapping is empty, try to init from first table
        if (columnMapping.length === 0) {
            const firstTable = unionTables.find(t => t);
            if (firstTable) {
                const cols = getTableColumns(firstTable);
                const newMap = cols.map(c => {
                    const sources = unionTables.map(t => {
                        if (!t) return '';
                        const tCols = getTableColumns(t);
                        return tCols.includes(c) ? c : '';
                    });
                    return { target: c, sources };
                });
                setColumnMapping(newMap);
            }
        } else {
            // If mapping exists, try to fill holes for newly selected tables
            const newMap = columnMapping.map(row => {
                const newSources = [...row.sources];
                // Ensure sources array length matches tables
                while (newSources.length < unionTables.length) newSources.push('');

                unionTables.forEach((t, idx) => {
                    if (t && !newSources[idx]) {
                        const tCols = getTableColumns(t);
                        // Case-insensitive match or exact? Exact for now.
                        if (tCols.includes(row.target)) {
                            newSources[idx] = row.target;
                        }
                    } else if (!t) {
                        newSources[idx] = '';
                    }
                });
                return { ...row, sources: newSources };
            });
            // check if deep equal before setting to avoid infinite loop
            if (JSON.stringify(newMap) !== JSON.stringify(columnMapping)) {
                setColumnMapping(newMap);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [combineMode, unionTables]);

    const addJoin = () => {
        setJoins([...joins, { id: Date.now(), type: 'inner', table: '', leftKey: '', rightKey: '' }]);
    };

    const removeJoin = (index) => {
        const newJoins = [...joins];
        newJoins.splice(index, 1);
        setJoins(newJoins);
    };

    const updateJoin = (index, field, value) => {
        const newJoins = [...joins];
        newJoins[index][field] = value;
        setJoins(newJoins);
    };



    const handlePreview = async () => {
        setLoading(true);
        setError('');
        try {
            let endpoint = combineMode === 'join' ? '/data/preview-join' : '/data/preview-union';
            let body = {};

            if (combineMode === 'join') {
                if (!baseTable) {
                    setError('Please select a base dataset.');
                    setLoading(false);
                    return;
                }

                // Prepare joins payload
                // Each join needs: table, type, condition ("Base.col = Join.col")
                const processedJoins = joins.map(j => {
                    if (!j.table || !j.leftKey || !j.rightKey) {
                        throw new Error('Incomplete join configuration');
                    }
                    // Construct condition. We use table names explicitly for clarity
                    // Backend supports multiple joins now
                    return {
                        table: j.table,
                        type: j.type,
                        condition: `"${baseTable}"."${j.leftKey}" = "${j.table}"."${j.rightKey}"`
                        // Note: Logic above assumes joining back to Base Table. 
                        // Ideally user selects WHICH table to join to (Base or Previous Join). 
                        // For simplicity in UI, we default 'leftKey' to come from Base Table or we can allow user to pick table.
                        // Implementation tweak below: The UI allows picking 'leftKey' from Base Table columns.
                        // If we want to support joining to previous tables, we need more UI. 
                        // Current 'leftKey' comes from getTableColumns(baseTable).
                    };
                });

                body = {
                    baseTable,
                    joins: processedJoins
                };
            } else {
                const selectedTables = unionTables.filter(t => t);
                if (selectedTables.length < 2) {
                    setError('Please select at least 2 datasets for union.');
                    setLoading(false);
                    return;
                }
                body = {
                    tables: unionTables,
                    columnMappings: columnMapping.map(m => ({
                        target: m.target,
                        sourceColumns: m.sources
                    }))
                };
            }

            // Using fetch with credentials
            const res = await fetch(`${API_BASE}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body)
            });

            const data = await res.json();
            if (res.ok) {
                setPreviewData(data.preview);
                setGeneratedSql(data.sql);
                setStep(2);
            } else {
                setError(data.error || 'Failed to preview data');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!newDatasetName.trim()) {
            setError('Please enter a name for the new dataset.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/data/create-view`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    viewName: newDatasetName,
                    sql: generatedSql
                })
            });

            const data = await res.json();
            if (res.ok) {
                onSaveDataset && onSaveDataset(); // Refresh main list
                onClose();
            } else {
                setError(data.error || 'Failed to create dataset');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="style-modal-overlay">
            <div className="style-modal-content" style={{ maxWidth: '800px', width: '90%' }}>
                {/* Header */}
                <div className="style-modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="icon-box-sm">
                            <Combine size={20} color="#8b5cf6" />
                        </div>
                        <div>
                            <h3>Combine Datasets</h3>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                                {step === 1 ? 'Configure Join or Union logic' : 'Preview and Save Dataset'}
                            </p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="style-modal-body" style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
                    {error && (
                        <div style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '16px',
                            fontSize: '0.9rem'
                        }}>
                            {error}
                        </div>
                    )}

                    {step === 1 ? (
                        <div className="combine-config-step">
                            {/* Mode Selection */}
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                                <button
                                    onClick={() => setCombineMode('join')}
                                    className={combineMode === 'join' ? 'btn-primary-premium' : 'btn-secondary-premium'}
                                    style={{ flex: 1, justifyContent: 'center' }}
                                >
                                    <Link2 size={16} style={{ marginRight: '8px' }} /> Join (Merge Columns)
                                </button>
                                <button
                                    onClick={() => setCombineMode('union')}
                                    className={combineMode === 'union' ? 'btn-primary-premium' : 'btn-secondary-premium'}
                                    style={{ flex: 1, justifyContent: 'center' }}
                                >
                                    <Layers size={16} style={{ marginRight: '8px' }} /> Union (Stack Rows)
                                </button>
                            </div>

                            {/* JOIN CONFIGURATION */}
                            {combineMode === 'join' && (
                                <div className="join-config-container">
                                    {/* Base Table */}
                                    <div className="form-group" style={{ marginBottom: '24px' }}>
                                        <label style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>1. Start with Base Dataset</label>
                                        <select
                                            className="premium-input"
                                            value={baseTable}
                                            onChange={e => {
                                                setBaseTable(e.target.value);
                                                setJoins([]); // Clear joins if base changes to avoid conflicts
                                            }}
                                            style={{ marginTop: '8px' }}
                                        >
                                            <option value="">Select Base Table...</option>
                                            {tables.map(t => (
                                                <option key={t.name} value={t.name}>{t.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Joins List */}
                                    {baseTable && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            {joins.map((join, idx) => (
                                                <div key={join.id} className="join-row-card" style={{
                                                    background: 'var(--bg-tertiary)',
                                                    padding: '16px',
                                                    borderRadius: '12px',
                                                    border: '1px solid var(--glass-border)',
                                                    position: 'relative'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', borderBottom: '1px solid var(--glass-border)', paddingBottom: '8px' }}>
                                                        <span style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{ width: '24px', height: '24px', background: 'var(--accent-primary)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>
                                                                {idx + 1}
                                                            </div>
                                                            Join Configuration
                                                        </span>
                                                        <button
                                                            onClick={() => removeJoin(idx)}
                                                            className="text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                                                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444' }}
                                                            title="Remove Join"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px', marginBottom: '16px' }}>
                                                        {/* Join Type */}
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: 500 }}>Type</label>
                                                            <select
                                                                className="premium-input"
                                                                value={join.type}
                                                                onChange={e => updateJoin(idx, 'type', e.target.value)}
                                                                style={{ padding: '10px', width: '100%' }}
                                                            >
                                                                <option value="inner">Inner Join</option>
                                                                <option value="left">Left Join</option>
                                                                <option value="right">Right Join</option>
                                                                <option value="full">Full Join</option>
                                                            </select>
                                                        </div>

                                                        {/* Table to Join */}
                                                        <div>
                                                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '6px', fontWeight: 500 }}>Join With Table (Right Side)</label>
                                                            <select
                                                                className="premium-input"
                                                                value={join.table}
                                                                onChange={e => updateJoin(idx, 'table', e.target.value)}
                                                                style={{ padding: '10px', width: '100%' }}
                                                            >
                                                                <option value="">Select Table to Join...</option>
                                                                {tables.filter(t => t.name !== baseTable).map(t => (
                                                                    <option key={t.name} value={t.name}>{t.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    {/* Condition */}
                                                    {join.table && (
                                                        <div style={{ background: 'var(--bg-secondary)', padding: '12px', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
                                                            <label style={{ display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 700 }}>Join Condition</label>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 30px 1fr', alignItems: 'center', gap: '8px' }}>

                                                                {/* LEFT COLUMN */}
                                                                <div>
                                                                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '4px' }}>{baseTable} Column</label>
                                                                    <select
                                                                        className="premium-input"
                                                                        value={join.leftKey}
                                                                        onChange={e => updateJoin(idx, 'leftKey', e.target.value)}
                                                                        style={{ padding: '8px', width: '100%', fontSize: '0.9rem' }}
                                                                    >
                                                                        <option value="">Select Column...</option>
                                                                        {getTableColumns(baseTable).map(c => (
                                                                            <option key={c} value={c}>{c}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>

                                                                {/* OPERATOR */}
                                                                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '18px', color: 'var(--text-muted)', fontWeight: 600 }}>
                                                                    =
                                                                </div>

                                                                {/* RIGHT COLUMN */}
                                                                <div>
                                                                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-dim)', marginBottom: '4px' }}>{join.table} Column</label>
                                                                    <select
                                                                        className="premium-input"
                                                                        value={join.rightKey}
                                                                        onChange={e => updateJoin(idx, 'rightKey', e.target.value)}
                                                                        style={{ padding: '8px', width: '100%', fontSize: '0.9rem' }}
                                                                    >
                                                                        <option value="">Select Column...</option>
                                                                        {getTableColumns(join.table).map(c => (
                                                                            <option key={c} value={c}>{c}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            <button
                                                className="btn-secondary-premium"
                                                onClick={addJoin}
                                                style={{ borderStyle: 'dashed', justifyContent: 'center' }}
                                            >
                                                <Plus size={16} style={{ marginRight: '8px' }} /> Add Another Join
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* UNION CONFIGURATION */}
                            {combineMode === 'union' && (
                                <div className="combine-config-step">
                                    <div style={{ marginBottom: '24px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                            <label style={{ fontWeight: 600, color: 'var(--accent-primary)' }}>Select Datasets to Union (Stack)</label>
                                            <button
                                                className="btn-secondary-premium"
                                                onClick={() => setUnionTables([...unionTables, ''])}
                                                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                                            >
                                                <Plus size={14} style={{ marginRight: '6px' }} /> Add Table
                                            </button>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {unionTables.map((table, idx) => (
                                                <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                    <div style={{
                                                        width: '24px', height: '24px',
                                                        background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)',
                                                        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-dim)'
                                                    }}>
                                                        {idx + 1}
                                                    </div>
                                                    <select
                                                        className="premium-input"
                                                        value={table}
                                                        onChange={e => {
                                                            const newTables = [...unionTables];
                                                            newTables[idx] = e.target.value;
                                                            setUnionTables(newTables);
                                                        }}
                                                        style={{ flex: 1 }}
                                                    >
                                                        <option value="">Select Table...</option>
                                                        {tables.map(t => (
                                                            <option key={t.name} value={t.name}>{t.name}</option>
                                                        ))}
                                                    </select>
                                                    {unionTables.length > 2 && (
                                                        <button
                                                            onClick={() => {
                                                                const newTables = unionTables.filter((_, i) => i !== idx);
                                                                setUnionTables(newTables);
                                                                // Also remove from mapping
                                                                const newMap = columnMapping.map(m => ({
                                                                    ...m,
                                                                    sources: m.sources.filter((_, i) => i !== idx)
                                                                }));
                                                                setColumnMapping(newMap);
                                                            }}
                                                            style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {unionTables.filter(t => t).length >= 1 && (
                                        <div className="union-config-pnl" style={{ background: 'var(--bg-tertiary)', padding: '20px', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                <label style={{ fontWeight: 600 }}>Field Mapping</label>
                                                <button
                                                    onClick={() => {
                                                        const newSources = unionTables.map(() => '');
                                                        setColumnMapping([...columnMapping, { target: 'new_column', sources: newSources }]);
                                                    }}
                                                    style={{ fontSize: '0.8rem', color: 'var(--accent-primary)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                                                >
                                                    + Add Column
                                                </button>
                                            </div>

                                            <div style={{ overflowX: 'auto' }}>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `1.5fr repeat(${unionTables.length}, 1fr) 30px`,
                                                    gap: '12px', marginBottom: '12px', minWidth: '600px'
                                                }}>
                                                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-dim)' }}>Output Name</div>
                                                    {unionTables.map((t, i) => (
                                                        <div key={i} style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {t || `Table ${i + 1}`}
                                                        </div>
                                                    ))}
                                                    <div></div>
                                                </div>

                                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                    {columnMapping.map((map, rowIdx) => (
                                                        <div key={rowIdx} style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: `1.5fr repeat(${unionTables.length}, 1fr) 30px`,
                                                            gap: '12px', marginBottom: '8px', minWidth: '600px', alignItems: 'center'
                                                        }}>
                                                            <input
                                                                type="text"
                                                                className="premium-input"
                                                                value={map.target}
                                                                onChange={e => {
                                                                    const newMap = [...columnMapping];
                                                                    newMap[rowIdx].target = e.target.value;
                                                                    setColumnMapping(newMap);
                                                                }}
                                                                style={{ padding: '8px', fontSize: '0.9rem' }}
                                                            />

                                                            {map.sources.map((src, colIdx) => (
                                                                <select
                                                                    key={colIdx}
                                                                    className="premium-input"
                                                                    value={src || ''}
                                                                    onChange={e => {
                                                                        const newMap = [...columnMapping];
                                                                        newMap[rowIdx].sources[colIdx] = e.target.value;
                                                                        setColumnMapping(newMap);
                                                                    }}
                                                                    style={{ padding: '8px', fontSize: '0.9rem' }}
                                                                >
                                                                    <option value="">(Null)</option>
                                                                    {getTableColumns(unionTables[colIdx]).map(c => (
                                                                        <option key={c} value={c}>{c}</option>
                                                                    ))}
                                                                </select>
                                                            ))}

                                                            <button
                                                                onClick={() => {
                                                                    const newMap = columnMapping.filter((_, i) => i !== rowIdx);
                                                                    setColumnMapping(newMap);
                                                                }}
                                                                style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                    ) : (
                        <div className="preview-step">
                            <div className="form-group" style={{ marginBottom: '24px' }}>
                                <label style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>New Dataset Name</label>
                                <input
                                    type="text"
                                    className="premium-input"
                                    placeholder="e.g. joined_customer_sales"
                                    value={newDatasetName}
                                    onChange={e => setNewDatasetName(e.target.value)}
                                    autoFocus
                                />
                                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                    This will create a virtual table (view) in your database.
                                </p>
                            </div>

                            <label style={{ marginBottom: '12px', display: 'block', fontWeight: 600 }}>Data Preview (First 10 Rows)</label>
                            <div className="table-container" style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid var(--glass-border)', borderRadius: '8px' }}>
                                {previewData.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                        <thead>
                                            <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--glass-border)' }}>
                                                {Object.keys(previewData[0]).map((key) => (
                                                    <th key={key} style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-dim)' }}>{key}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewData.map((row, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                                    {Object.values(row).map((val, j) => (
                                                        <td key={j} style={{ padding: '12px', color: 'var(--text-main)' }}>
                                                            {val === null ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>null</span> : String(val)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>No data returned</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="style-modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    {step === 2 && (
                        <button
                            className="btn-secondary-premium"
                            onClick={() => setStep(1)}
                            disabled={loading}
                        >
                            Back
                        </button>
                    )}

                    {step === 1 ? (
                        <button
                            className="btn-primary-premium"
                            onClick={handlePreview}
                            disabled={loading || (combineMode === 'join' ? !baseTable : (unionTables.filter(t => t).length < 2))}
                        >
                            {loading ? 'Generating Preview...' : 'Next: Preview Data'}
                        </button>
                    ) : (
                        <button
                            className="btn-primary-premium"
                            onClick={handleSave}
                            disabled={loading}
                            style={{ background: 'var(--accent-secondary)' }}
                        >
                            {loading ? 'Saving...' : 'Create Dataset'} <Save size={16} style={{ marginLeft: '8px' }} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DataCombineModal;
